// Lib
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';

// Selectors
import { getCurrentlyEditingEditorKey } from '../../element/selection/currentlyEditingSelector';
import { getPlatformDetailsSelector } from '../../platform/platformSelector';

// Hooks
import useSimulateMobileKeyboard from './useSimulateMobileKeyboard';
import { useVirtualKeyboardSetHeightContext } from './VirtualKeyboardHeightContext';
import useVirtualKeyboardFinishEditingHandler from './useVirtualKeyboardFinishEditingHandler';

// Utils
import { KeyboardPlugin } from '../../../capacitor_plugins/keyboard';
import virtualKeyboardHeightSingleton from './virtualKeyboardHeightSingleton';
import globalLogger from '../../logger/logger';

// Constants
import { CAPACITOR_MN_KEYBOARD_PLUGIN } from '../../../capacitor_plugins/pluginConstants';
import localStorageService from '../services/localStorage/localStorage';
import { VIRTUAL_KEYBOARD_HEIGHT_STORAGE_KEY } from './useSaveVirtualKeyboardHeightToLocalStorage';
import { DeviceFormFactor } from '../../../common/platform/platformTypes';

const KEYBOARD_REVEAL_DURATION = 300;

const logger = globalLogger.createChannel('virtual-keyboard-handler');

/**
 * Listens to changes in the visual viewport to dispatch the following events:
 * - keyboardDidShow
 * - keyboardWillHide
 */
export const useVirtualKeyboardEventHandler = (): void => {
    const editorKey = useSelector(getCurrentlyEditingEditorKey);
    const isEditingElement = !!editorKey;
    const platformDetails = useSelector(getPlatformDetailsSelector);

    // The assumption here is that non-desktop devices use virtual keyboards
    const isEnabled = platformDetails.formFactor !== DeviceFormFactor.desktop;

    const setVirtualKeyboardHeight = useVirtualKeyboardSetHeightContext();

    const isKeyboardRevealed = useRef<boolean>(false);

    const updateKeyboardHeight = (keyboardHeight: number) => {
        setVirtualKeyboardHeight(keyboardHeight);
        virtualKeyboardHeightSingleton.setHeight(keyboardHeight);

        if (keyboardHeight !== 0) {
            document.body.style.setProperty('--keyboard-open-height', `${keyboardHeight}px`);
        }
    };

    useVirtualKeyboardFinishEditingHandler({ enabled: isEnabled });

    const setInitialKeyboardHeight = async () => {
        let keyboardHeight = 0;

        if (Capacitor.isPluginAvailable(CAPACITOR_MN_KEYBOARD_PLUGIN)) {
            try {
                keyboardHeight = (await KeyboardPlugin.getKeyboardHeight()).keyboardHeight;
            } catch (error) {
                // Sensitive data audit: SAFE — error auto-sanitized (static message)
                logger.error('Failed to set initial keyboard height', error);
            }
        } else {
            const keyboardHeightFromLocalStorage = localStorageService.getItem(VIRTUAL_KEYBOARD_HEIGHT_STORAGE_KEY);
            keyboardHeight = keyboardHeightFromLocalStorage
                ? parseInt(keyboardHeightFromLocalStorage, 10)
                : keyboardHeight;
        }

        virtualKeyboardHeightSingleton.setInitialMaxHeight(keyboardHeight);
        document.body.style.setProperty('--keyboard-open-height', `${keyboardHeight}px`);
    };

    useEffect(() => {
        if (!isEnabled) return;

        setInitialKeyboardHeight();
    }, [isEnabled]);

    const onKeyboardWillShow = (keyboardHeight: number) => {
        requestAnimationFrame(() => {
            isKeyboardRevealed.current = true;

            updateKeyboardHeight(keyboardHeight);
            // On Android we can receive the keyboard height before the "did show" callback is reliable.
            // Applying the class here ensures keyboard-open CSS variables take effect for the whole reveal.
            document.body.classList.add('keyboard-open');

            window.dispatchEvent(new CustomEvent('keyboardWillShow', { detail: { keyboardHeight: keyboardHeight } }));
        });
    };

    const onKeyboardDidShow = (keyboardHeight: number) => {
        requestAnimationFrame(() => {
            isKeyboardRevealed.current = true;

            document.body.classList.add('keyboard-open');

            window.dispatchEvent(new CustomEvent('keyboardDidShow', { detail: { keyboardHeight: keyboardHeight } }));
        });
    };

    const onKeyboardWillHide = () => {
        requestAnimationFrame(() => {
            updateKeyboardHeight(0);

            isKeyboardRevealed.current = false;

            document.body.classList.remove('keyboard-open');

            window.dispatchEvent(new CustomEvent('keyboardWillHide'));
        });
    };

    const onKeyboardDidHide = () => {
        requestAnimationFrame(() => {
            updateKeyboardHeight(0);

            isKeyboardRevealed.current = false;

            document.body.classList.remove('keyboard-open');

            window.dispatchEvent(new CustomEvent('keyboardDidHide'));
        });
    };

    const simulateMobileKeyboard = useSimulateMobileKeyboard({
        onKeyboardDidShow,
        onKeyboardWillHide,
        onKeyboardWillShow,
    });

    // FIXME: Capacitor.isPluginAvailable(CAPACITOR_MN_KEYBOARD_PLUGIN) returns false in live-reload-ngrok mode, which means that
    //        the toolbar does not get correctly revealed in this mode (https://linear.app/milanote/issue/MOB-1672)
    // On Capacitor apps, use the Keyboard Capacitor plugin to listen to keyboardWillShow event
    useEffect(() => {
        if (!isEnabled) return;

        if (Capacitor.isPluginAvailable(CAPACITOR_MN_KEYBOARD_PLUGIN)) {
            KeyboardPlugin.removeAllListeners();

            KeyboardPlugin.addListener('keyboardWillShow', (info) => onKeyboardWillShow(info.keyboardHeight));
            KeyboardPlugin.addListener('keyboardDidShow', (info) => onKeyboardDidShow(info.keyboardHeight));
            KeyboardPlugin.addListener('keyboardWillHide', () => onKeyboardWillHide());
            KeyboardPlugin.addListener('keyboardDidHide', () => onKeyboardDidHide());

            return () => {
                KeyboardPlugin.removeAllListeners();
            };
        }
    }, [isEnabled]);

    const recalculateKeyboardPositionOnBrowser = () => {
        if (!window.visualViewport) return;

        const keyboardHeight = window.innerHeight - window.visualViewport.height;

        // Reveal keyboard after 300ms timeout to wait until keyboard has fully shown
        const isRevealingKeyboard = !isKeyboardRevealed.current && keyboardHeight > 0;
        if (isRevealingKeyboard) {
            onKeyboardWillShow(keyboardHeight);
            setTimeout(() => onKeyboardDidShow(keyboardHeight), KEYBOARD_REVEAL_DURATION);
        }

        // Update keyboard height if it's already shown
        // this can occur when the auto-correct bar is shown/hidden
        const isUpdatingKeyboardHeight = isKeyboardRevealed.current && keyboardHeight > 0;
        if (isUpdatingKeyboardHeight) {
            updateKeyboardHeight(keyboardHeight);
        }

        const isHidingKeyboard = isKeyboardRevealed.current && keyboardHeight === 0;
        if (isHidingKeyboard) onKeyboardWillHide();
    };

    useEffect(() => {
        if (!isEnabled) return;
        if (simulateMobileKeyboard) return;
        // Toolbar is always hidden on cleanup so if we're not editing, we don't need to do anything
        if (!isEditingElement) return;

        // On Capacitor apps, also hide keyboard when isEditingElement updates to false
        //
        // NOTE: This is doing the same thing as the keyboardWillHide event listener above, with the idea that whichever
        //   comes first will hide the keyboard, as each method can be a bit late in timing the keyboard hide event
        //   depending on situation.
        if (Capacitor.isPluginAvailable(CAPACITOR_MN_KEYBOARD_PLUGIN)) {
            return () => {
                onKeyboardWillHide();
            };
        }

        // On non-Capacitor apps, manually calculate the keyboard position on resize/scroll events

        recalculateKeyboardPositionOnBrowser();

        window.visualViewport?.addEventListener('resize', recalculateKeyboardPositionOnBrowser);

        return () => {
            window.visualViewport?.removeEventListener('resize', recalculateKeyboardPositionOnBrowser);

            onKeyboardWillHide();
        };
    }, [isEnabled, isEditingElement, simulateMobileKeyboard]);
};

const VirtualKeyboardEventManager = () => {
    useVirtualKeyboardEventHandler();
    return null;
};

export default VirtualKeyboardEventManager;
